import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apolloClient } from "../apollo/client";

export type TenantSummary = {
  id: string;
  name: string;
  domain: string;
};

export type AuthUser = {
  id: string;
  email: string;
  name?: string;
  tenant_id: string | null;
  tenant: TenantSummary | null;
  roles: string[];
  is_superadmin: boolean;
  permissions?: string[];
  impersonating?: {
    id: string;
    email: string;
    tenant_id: string | null;
    tenant: TenantSummary | null;
  };
};

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  setUser: (user: AuthUser | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/auth/check", {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        setUser(null);
        return;
      }
      const data = await response.json();
      setUser(data.authenticated ? (data.user as AuthUser) : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Even if the network call fails, clear local state so the user is
      // bounced to /login. The cookie may still be valid server-side, but
      // RequireAuth will re-check on next mount.
    } finally {
      setUser(null);
      // Drop all cached tenant data so the next account that signs in on this
      // browser never sees the previous tenant's data (e.g. branding).
      try {
        await apolloClient.clearStore();
      } catch {
        // clearStore() doesn't refetch, but never let cleanup break logout.
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{ user, loading, setUser, refresh, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

/**
 * The tenant the current session is acting on — the impersonated tenant
 * when impersonating, otherwise the signed-in user's own tenant — or null
 * when the session hasn't resolved yet (no user) or carries no tenant.
 *
 * Use this in chrome and providers that render *around* the auth gate
 * (titles, sidebar lockup, branding) and must degrade gracefully. Feature
 * screens under <RequireAuth> should use `useActiveTenant()` instead.
 */
export function useActiveTenantOrNull(): TenantSummary | null {
  const { user } = useAuth();
  return user?.impersonating?.tenant ?? user?.tenant ?? null;
}

/**
 * Same resolution as `useActiveTenantOrNull`, but asserts a tenant exists.
 *
 * Both `/auth/login` and `/auth/check` always include the tenant, so any
 * authenticated session has one. Callers live under <RequireAuth>, so this
 * throws rather than returning a fallback — a missing tenant is a bug to
 * surface, never a value to paper over with a hardcoded domain.
 */
export function useActiveTenant(): TenantSummary {
  const tenant = useActiveTenantOrNull();
  if (!tenant) {
    throw new Error(
      "useActiveTenant: authenticated session has no tenant — check the /auth payload",
    );
  }
  return tenant;
}
